/**
 * SECCIÓN B — Vista Global SUPER_ADMIN para módulos del sidebar.
 * Componente genérico reutilizable. Solo READ/DOWNLOAD.
 * Sin botones de crear/editar/eliminar.
 *
 * Uso:
 *   if (user.role === "SUPER_ADMIN")
 *     return <SuperAdminModuleView moduleName="Residuos" apiEndpoint="/superadmin/overview/waste_logs" />;
 *
 * Backend espera devolver:
 *   { module, generated_at, total_clients, total_documents,
 *     clients: [{ tenant_id, client_name, plan, status, total_users, total_records,
 *                 users: [{ user_id, user_name, role, records_count,
 *                           records: [{id,title,status,created_at,updated_at,has_attachments,download_url}] }] }] }
 */
import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { ChevronDown, ChevronRight, Download, Eye, Database, Users } from "lucide-react";

export default function SuperAdminModuleView({ moduleName, apiEndpoint, downloadAllEndpoint }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [openClients, setOpenClients] = useState({});
  const [openUsers, setOpenUsers] = useState({});

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(apiEndpoint).then((r) => {
      if (cancelled) return;
      setData(r.data || { clients: [] });
      setLoading(false);
    }).catch((e) => {
      if (cancelled) return;
      setError(e?.response?.data?.detail || e.message || "No se pudo cargar la vista global");
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [apiEndpoint]);

  async function downloadBlob(url, filename) {
    try {
      const res = await api.get(url, { responseType: "blob" });
      const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);
    } catch (e) {
      alert("No se pudo descargar: " + (e?.response?.data?.detail || e.message));
    }
  }

  if (loading) return <div className="card text-dim" data-testid="superadmin-loading">Cargando vista global…</div>;
  if (error) return <div className="card" style={{ borderColor: "var(--danger)" }} data-testid="superadmin-error">{error}</div>;
  if (!data) return null;

  const clients = data.clients || [];

  return (
    <div data-testid="superadmin-module-view" className="space-y-4">
      <div className="card">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="card-title">Vista Global — {moduleName}</div>
            <div className="text-xs text-dim mt-1">
              <span className="badge badge-blue">SUPER_ADMIN · Solo lectura</span>
              {data.generated_at && <span className="ml-2">Generado: {new Date(data.generated_at).toLocaleString("es-MX")}</span>}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="kpi-card" style={{ minWidth: 120 }}>
              <div className="label flex items-center gap-1"><Users size={12}/> Clientes</div>
              <div className="value">{data.total_clients ?? clients.length}</div>
            </div>
            <div className="kpi-card" style={{ minWidth: 120 }}>
              <div className="label flex items-center gap-1"><Database size={12}/> Registros</div>
              <div className="value">{data.total_documents ?? 0}</div>
            </div>
            {downloadAllEndpoint && (
              <button
                className="btn btn-primary"
                onClick={() => downloadBlob(downloadAllEndpoint, `${moduleName}_global.xlsx`)}
                data-testid="superadmin-export-all"
              >
                <Download size={14}/> Exportar Todo
              </button>
            )}
          </div>
        </div>
      </div>

      {clients.length === 0 && <div className="card text-dim">Sin clientes con datos en este módulo.</div>}

      {clients.map((c) => {
        const cOpen = !!openClients[c.tenant_id];
        return (
          <div key={c.tenant_id} className="card" data-testid={`superadmin-client-${c.tenant_id}`}>
            <button
              className="flex items-center justify-between w-full"
              style={{ background: "transparent", border: 0, padding: 0, cursor: "pointer" }}
              onClick={() => setOpenClients((s) => ({ ...s, [c.tenant_id]: !cOpen }))}
            >
              <div className="flex items-center gap-2">
                {cOpen ? <ChevronDown size={16}/> : <ChevronRight size={16}/>}
                <b>{c.client_name}</b>
                <span className="badge badge-gray">{c.plan || "Basic"}</span>
                <span className="badge badge-green">{c.status || "Activo"}</span>
              </div>
              <div className="text-xs text-dim">
                {c.total_users || 0} usuarios · {c.total_records || 0} registros
              </div>
            </button>

            {cOpen && (
              <div className="mt-3 space-y-2">
                {c.download_url && (
                  <div>
                    <button
                      className="btn btn-sm"
                      onClick={() => downloadBlob(c.download_url, `${c.client_name}_${moduleName}.xlsx`)}
                      data-testid={`superadmin-download-client-${c.tenant_id}`}
                    >
                      <Download size={12}/> Descargar datos de este cliente
                    </button>
                  </div>
                )}

                {(c.users || []).map((u) => {
                  const uKey = `${c.tenant_id}_${u.user_id}`;
                  const uOpen = !!openUsers[uKey];
                  return (
                    <div key={uKey} className="card" style={{ background: "var(--bg-2)" }}>
                      <button
                        className="flex items-center justify-between w-full"
                        style={{ background: "transparent", border: 0, padding: 0, cursor: "pointer" }}
                        onClick={() => setOpenUsers((s) => ({ ...s, [uKey]: !uOpen }))}
                      >
                        <div className="flex items-center gap-2">
                          {uOpen ? <ChevronDown size={14}/> : <ChevronRight size={14}/>}
                          <span>{u.user_name}</span>
                          <span className="badge badge-gray">{u.role}</span>
                        </div>
                        <div className="text-xs text-dim">{u.records_count || 0} registros</div>
                      </button>
                      {uOpen && (
                        <div className="mt-2">
                          {u.download_url && (
                            <button
                              className="btn btn-sm mb-2"
                              onClick={() => downloadBlob(u.download_url, `${u.user_name}_${moduleName}.xlsx`)}
                              data-testid={`superadmin-download-user-${u.user_id}`}
                            >
                              <Download size={12}/> Descargar datos de este usuario
                            </button>
                          )}
                          <div className="table-wrap" style={{ overflowX: "auto" }}>
                            <table className="table">
                              <thead>
                                <tr>
                                  <th>Título</th>
                                  <th>Estado</th>
                                  <th>Creado</th>
                                  <th>Actualizado</th>
                                  <th>Acciones</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(u.records || []).map((r) => (
                                  <tr key={r.id}>
                                    <td>{r.title || r.id}</td>
                                    <td>{r.status || "-"}</td>
                                    <td className="text-xs">{r.created_at ? new Date(r.created_at).toLocaleDateString("es-MX") : "-"}</td>
                                    <td className="text-xs">{r.updated_at ? new Date(r.updated_at).toLocaleDateString("es-MX") : "-"}</td>
                                    <td>
                                      <div className="flex gap-1">
                                        <button className="btn btn-sm" title="Ver" data-testid={`superadmin-view-${r.id}`}>
                                          <Eye size={12}/>
                                        </button>
                                        {r.download_url && (
                                          <button
                                            className="btn btn-sm"
                                            title="Descargar"
                                            onClick={() => downloadBlob(r.download_url, `${r.title || r.id}.pdf`)}
                                            data-testid={`superadmin-dl-${r.id}`}
                                          >
                                            <Download size={12}/>
                                          </button>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
